import React, { useState, useEffect } from 'react';
import { complaintsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
// import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import Pagination from '@/components/ui/Pagination';
import { Eye, Edit, Trash2, Search, AlertTriangle, Clock, CheckCircle, XCircle, FileText } from 'lucide-react';
import { ComplaintViewModal } from './ComplaintViewModal';
import { ComplaintEditModal } from './ComplaintEditModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { useToast } from '@/components/ui/Toast';

interface Complaint {
  complaint_id: number;
  customer_contact: string;
  complaint_date: string;
  defect_description: string;
  severity: string;
  status: string;
  cost_impact_estimate: number;
  customer: { name?: string };
  product: { name?: string };
  batch: { batch_number?: string };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export const ComplaintsTab: React.FC = () => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    severity: '',
  });
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [complaintStats, setComplaintStats] = useState({
    total: 0,
    register: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0
  });
  const { addToast } = useToast();

  useEffect(() => {
    fetchComplaints();
  }, [pagination.page, filters]);

  useEffect(() => {
    fetchComplaintStats();
  }, []);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.severity && filters.severity !== 'all') params.severity = filters.severity;
      
      const response = await complaintsAPI.getAll(params);
      
      if (response.success) {
        setComplaints(response.data.complaints || []);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch complaints:', error);
      addToast({
        title: 'Error',
        message: 'Failed to fetch complaints',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchComplaintStats = async () => {
    try {
      // Fetch all complaints without pagination to get accurate stats
      const statsParams = { page: 1, limit: 1000 }; // Large limit to get all
      const response = await complaintsAPI.getAll(statsParams);
      
      if (response.success) {
        const allComplaints = response.data.complaints || [];
        const stats = {
          total: response.data.pagination.total,
          register: allComplaints.filter((c: Complaint) => c.status === 'Register').length,
          in_progress: allComplaints.filter((c: Complaint) => c.status === 'In Progress').length,
          resolved: allComplaints.filter((c: Complaint) => c.status === 'Resolved').length,
          closed: allComplaints.filter((c: Complaint) => c.status === 'Closed').length
        };
        setComplaintStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch complaint stats:', error);
    }
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusCardClick = (status: string) => {
    setFilters(prev => ({ ...prev, status: status === 'all' ? '' : status }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination(prev => ({ ...prev, limit: itemsPerPage, page: 1 }));
  };

  const handleView = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    setViewModalOpen(true);
  };

  const handleEdit = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    setEditModalOpen(true);
  };

  const handleDelete = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedComplaint) return;

    try {
      await complaintsAPI.delete(selectedComplaint.complaint_id);
      addToast({
        title: 'Success',
        message: 'Complaint deleted successfully',
        type: 'success',
      });
      setDeleteModalOpen(false);
      setSelectedComplaint(null);
      fetchComplaints();
      fetchComplaintStats(); // Refresh stats after deletion
    } catch (error) {
      addToast({
        title: 'Error',
        message: 'Failed to delete complaint',
        type: 'error',
      });
    }
  };

  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      await complaintsAPI.updateStatus(id, status);
      addToast({
        title: 'Success',
        message: 'Complaint status updated successfully',
        type: 'success',
      });
      fetchComplaints();
      fetchComplaintStats(); // Refresh stats after status update
    } catch (error) {
      addToast({
        title: 'Error',
        message: 'Failed to update complaint status',
        type: 'error',
      });
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Register': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Complaint Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card 
          className="bg-blue-50 dark:bg-blue-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('all')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Complaints</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{complaintStats.total}</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full">
                <FileText className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-orange-50 dark:bg-orange-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('Register')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Registered</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{complaintStats.register}</p>
              </div>
              <div className="bg-orange-500 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-yellow-50 dark:bg-yellow-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('In Progress')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">In Progress</p>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{complaintStats.in_progress}</p>
              </div>
              <div className="bg-yellow-500 p-3 rounded-full">
                <Clock className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-green-50 dark:bg-green-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('Resolved')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Resolved</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{complaintStats.resolved}</p>
              </div>
              <div className="bg-green-500 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-gray-50 dark:bg-gray-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('Closed')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Closed</p>
                <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">{complaintStats.closed}</p>
              </div>
              <div className="bg-gray-500 p-3 rounded-full">
                <XCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white dark:bg-gray-800 shadow-lg border-0 rounded-2xl dark:border dark:border-gray-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl font-semibold text-gray-900 dark:text-white">Complaints Management</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search complaints..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
              />
            </div>
            <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
              <SelectTrigger className="w-full md:w-48 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Register">Register</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.severity} onValueChange={(value) => handleFilterChange('severity', value)}>
              <SelectTrigger className="w-full md:w-48 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl">
                <SelectValue placeholder="Filter by Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 dark:bg-gray-700">
                      <TableHead className="font-semibold text-gray-900 dark:text-white">ID</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Customer</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Product</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Severity</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Status</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Cost Impact</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Date</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {complaints.map((complaint) => (
                      <TableRow key={complaint.complaint_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <TableCell className="font-medium">#{complaint.complaint_id}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{complaint.customer?.name || 'N/A'}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{complaint.customer_contact}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{complaint.product?.name || 'N/A'}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{complaint.batch?.batch_number || 'N/A'}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getSeverityColor(complaint.severity)}>
                            {complaint.severity}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(complaint.status)}>
                            {complaint.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          ₹{complaint.cost_impact_estimate.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {new Date(complaint.complaint_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(complaint)}
                              className="h-8 w-8 p-0 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(complaint)}
                              className="h-8 w-8 p-0 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(complaint)}
                              className="h-8 w-8 p-0 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  showItemsPerPage={true}
                  showCurrentRange={true}
                  maxVisiblePages={5}
                  className="mt-6"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {selectedComplaint && (
        <>
          <ComplaintViewModal
            complaint={selectedComplaint}
            open={viewModalOpen}
            onClose={() => {
              setViewModalOpen(false);
              setSelectedComplaint(null);
            }}
          />
          <ComplaintEditModal
            complaint={selectedComplaint}
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
              setSelectedComplaint(null);
            }}
            onStatusUpdate={handleStatusUpdate}
          />
          <DeleteConfirmModal
            open={deleteModalOpen}
            onClose={() => {
              setDeleteModalOpen(false);
              setSelectedComplaint(null);
            }}
            onConfirm={handleDeleteConfirm}
            title="Delete Complaint"
            message={`Are you sure you want to delete complaint #${selectedComplaint.complaint_id}? This action cannot be undone.`}
          />
        </>
      )}
    </div>
  );
};